import { useRef, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import {
   Box,
   Button,
   Flex,
   FormControl,
   FormLabel,
   Link,
   Switch,
   Text,
   VStack,
} from '@chakra-ui/react';
import { SignUpInput, subTextStyles } from './components/SignUpInput';
import { _js } from '@ifixit/localize';
import { _jsr } from 'Shared/translations_react';
import { FormError } from '../FormError';
import { CodeInput } from './components/CodeInput';
import { useRegisterCode } from './hooks/useRegisterCode';
import { Geo } from 'Shared/utils';

interface RegisterCodeProps {
   privacyPolicyUrl: string;
   email: string;
}

function SignUpSwitch({
   value,
   onChange,
   label,
   id,
}: {
   value: boolean;
   onChange: (value: boolean) => void;
   label: string;
   id: string;
}) {
   return (
      <>
         <Flex id={id} align="center" width="100%">
            <Box flex="1">
               <FormLabel margin={0} fontWeight="normal">
                  {label}
               </FormLabel>
            </Box>
            <Switch
               isChecked={value}
               onChange={e => onChange(e.target.checked)}
               aria-labelledby={id}
               marginLeft={2}
            />
         </Flex>
      </>
   );
}

function NewsletterField({
   value,
   onChange,
   id,
   label,
}: {
   value: boolean;
   onChange: (value: boolean) => void;
   id: string;
   label: string;
}) {
   const hasRunGeoLogic = useRef(false);

   useEffect(() => {
      if (!hasRunGeoLogic.current) {
         Geo.runEverywhereButEU(() => {
            onChange(true);
         });
         hasRunGeoLogic.current = true;
      }
   }, [onChange]);

   return (
      <FormControl>
         <SignUpSwitch value={value} onChange={onChange} label={label} id={id} />
      </FormControl>
   );
}

export const RegisterCode = ({ privacyPolicyUrl, email }: RegisterCodeProps) => {
   const {
      formValues,
      code,
      setCode,
      codeError,
      errors,
      touched,
      isSubmitting,
      handleInputChange,
      handleInputBlur,
      handleSubmit,
      recaptchaPlaceholderRef,
   } = useRegisterCode({ email, privacyPolicyUrl });

   const privacyPolicyLink = (
      <Link href={privacyPolicyUrl} color="gray.900">
         {_js('Privacy Policy')}
      </Link>
   );

   const termsLink = (
      <Link href="/Info/Terms_of_Use" color="gray.900">
         {/* Translators: "Terms" as in Terms of Use */}
         {_js('Terms')}
      </Link>
   );

   const emailFieldProvided = !!email;

   return (
      <VStack
         className="sign-up-wrapper"
         flexDirection="column"
         spacing={6}
         paddingY={8}
         paddingX={6}
         bgColor="white"
         borderWidth={1}
         borderRadius="base"
         fontSize="16px"
         width={{ base: '100%', md: '384px' }}
      >
         <form
            id="registerForm"
            onSubmit={e => {
               e.preventDefault();
               void handleSubmit();
            }}
            className="sign-up-form"
            style={{ width: '100%' }}
         >
            <VStack
               spacing={6}
               sx={{
                  '& > div': { width: '100%' },
               }}
            >
               <FormControl isInvalid={touched.email && Boolean(errors.email)}>
                  <SignUpInput
                     field={{
                        name: 'email',
                        value: formValues.email,
                        onChange: (e: ChangeEvent<HTMLInputElement>) =>
                           handleInputChange('email', e.target.value),
                        onBlur: () => handleInputBlur('email'),
                        type: 'email',
                        isRequired: true,
                        autoComplete: 'section-register email',
                        placeholder: 'albert@domain.com',
                     }}
                     label={_js('Email')}
                     id="reg-email"
                     isDisabled={emailFieldProvided}
                  />
                  <FormError>{errors.email as any}</FormError>
               </FormControl>
               <FormControl isInvalid={touched.name && Boolean(errors.name)}>
                  <SignUpInput
                     field={{
                        name: 'name',
                        value: formValues.name,
                        onChange: (e: ChangeEvent<HTMLInputElement>) =>
                           handleInputChange('name', e.target.value),
                        onBlur: () => handleInputBlur('name'),
                        type: 'text',
                        isRequired: true,
                        autoComplete: 'section-register name',
                        placeholder: 'Albert Einstein',
                        minLength: 3,
                        maxLength: 30,
                     }}
                     label={_js('Display Name')}
                     helperText={_js('This field will be visible to others.')}
                     id="reg-username"
                  />
                  <FormError>{errors.name as any}</FormError>
               </FormControl>
               <Box width="100%">
                  <CodeInput value={code} onChange={setCode} inputId="register-code-input" />
                  {codeError && (
                     <Text color="red.500" mt={2} fontSize="sm">
                        {codeError}
                     </Text>
                  )}
               </Box>
               <VStack spacing={4} align="flex-start" fontSize="md">
                  <Box fontWeight="medium">
                     {_js('Level up your repair skills by subscribing to:')}
                  </Box>
                  <NewsletterField
                     value={formValues.wantsDigestEmails}
                     onChange={value => handleInputChange('wantsDigestEmails', value)}
                     id="community-yes"
                     label={_js("Monthly highlights from iFixit's online community")}
                  />
                  <NewsletterField
                     value={formValues.wantsNewsEmails}
                     onChange={value => handleInputChange('wantsNewsEmails', value)}
                     id="newsletter-yes"
                     label={_js(
                        'Weekly repair tips, Right to Repair news, and first look access to teardowns'
                     )}
                  />
               </VStack>
               {App?.recaptchaSiteKey && (
                  <Box
                     ref={recaptchaPlaceholderRef}
                     className="showRegister js-captcha-container captcha-container"
                  ></Box>
               )}
               <Button
                  variant="cta"
                  width="full"
                  type="submit"
                  id="registerBtn"
                  isLoading={isSubmitting}
               >
                  {_js('Create Account')}
               </Button>
               <Text {...subTextStyles}>
                  {_jsr(
                     /* Translators: %1 and %2 are links to our Privacy Policy and Terms of Use */
                     'By joining iFixit, you agree to our %1 and %2.',
                     privacyPolicyLink,
                     termsLink
                  )}
               </Text>
            </VStack>
         </form>
      </VStack>
   );
};
